import { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import styles from './GameShareScreen.module.css';
import blablagame from '../blablagame.png';
import instagramIcon from '../../assets/images/icons/instagram.svg';
import telegramIcon from '../../assets/images/icons/telega.svg';
import whatsappIcon from '../../assets/images/icons/whatsapp.svg';
import facebookIcon from '../../assets/images/icons/facebook.svg';
import twitterIcon from '../../assets/images/icons/twitter.svg';
import { useAppConfig } from '../../context/AppConfigContext';
import { useAuth } from '../../context/AuthContext';
import { fetchData } from '../../api/api';

const GameShareScreen = ({ phrases, gameStartTime }) => {
  const { t, brandName, site } = useAppConfig();
  const { userId, isGuest } = useAuth();
  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [shareImageBase64, setShareImageBase64] = useState(null);
  const [finalShareText, setFinalShareText] = useState([]);
  const [refCode, setRefCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [isSharing, setIsSharing] = useState(false);

  const shareText =
    t.referrals?.share_text ||
    `I learn languages with ${brandName}. Premium is included for 7 days via my link.`;

  const shareTitle = 'Поделись результатом, получи VIP и заработай';

  const fallbackReferralLink = refCode ? `${site}/invite/${refCode}` : '';
  const activeReferralLink = referralLink || fallbackReferralLink;
  //const sharePayloadText = activeReferralLink ? `${shareText}\n${activeReferralLink}` : shareText;
  const sharePayloadText = shareText;

  const pluralize = (num, one, few, many) => {
    if (num % 10 === 1 && num % 100 !== 11) return one;
    if (num % 10 >= 2 && num % 10 <= 4 && (num % 100 < 10 || num % 100 >= 20)) return few;
    return many;
  };

  const getShareText = (baseLanguage, totalPhrases, gameDuration) => {
    const minutes = Math.max(1, Math.round(gameDuration / 60));

    if (baseLanguage === 'ru') {
      const phraseWord = pluralize(
        totalPhrases,
        'фразу',
        'фразы',
        'фраз',
      );
      const minuteWord = pluralize(
        minutes,
        'минуту',
        'минуты',
        'минут',
      );
      const phrasesPerMinute = totalPhrases / minutes;
      const coursesRate = 20 / 60;
      const efficiency = Math.max(1, (phrasesPerMinute / coursesRate).toFixed(1));

      return [
        `Мой результат: ${totalPhrases} ${phraseWord} за ${minutes} ${minuteWord}!`,
        `В ${efficiency} раз эффективнее, чем на курсах!`,
      ];
    }

    if (baseLanguage === 'uk') {
      const getPhraseWord = (n) => {
        if (n % 10 === 1 && n % 100 !== 11) return 'фразу';
        if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return 'фрази';
        return 'фраз';
      };

      const getMinuteWord = (n) => {
        if (n % 10 === 1 && n % 100 !== 11) return 'хвилину';
        if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return 'хвилини';
        return 'хвилин';
      };

      const phraseWord = getPhraseWord(totalPhrases);
      const minuteWord = getMinuteWord(minutes);
      const phrasesPerMinute = totalPhrases / minutes;
      const coursesRate = 20 / 60;
      const efficiency = Math.max(1, (phrasesPerMinute / coursesRate).toFixed(1));

      return [
        `Мій результат: ${totalPhrases} ${phraseWord} за ${minutes} ${minuteWord}!`,
        `У ${efficiency} раз${efficiency > 1 ? 'и' : ''} ефективніше, ніж на курсах!`,
      ];
    }

    if (baseLanguage === 'de') {
      return [`Mein Ergebnis: ${totalPhrases} Sätze in ${minutes} Minuten!`];
    }

    if (baseLanguage === 'en') {
      return [`My result: ${totalPhrases} sentences in ${minutes} minutes!`];
    }

    return [`I spoke ${totalPhrases} sentences in ${minutes} minutes.`];
  };

  useEffect(() => {
    const gameEndTime = Date.now();
    const gameDuration = gameStartTime ? (gameEndTime - gameStartTime) / 1000 : 0;
    const text = getShareText('ru', phrases, gameDuration);
    setFinalShareText(text);
  }, [phrases, gameStartTime]);

  useEffect(() => {
    let objectUrl = null;

    const createShareImage = async () => {
      const img = new Image();
      img.src = blablagame;
      await img.decode();

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      ctx.drawImage(img, 0, 0);
      ctx.textAlign = 'left';
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      ctx.shadowBlur = 10;

      const maxWidth = 380;
      const x = 15;
      let y = 180;

      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.font = '42px Arial Black';

      const lineHeightBig = 50;
      const firstLineWords = String(finalShareText[0] || '').split(' ');
      let firstLine = '';
      let firstLineCount = 0;

      firstLineWords.forEach((word, index) => {
        const testLine = firstLine + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && index > 0) {
          const lineY = y + firstLineCount * lineHeightBig;
          ctx.strokeText(firstLine, x, lineY, maxWidth);
          ctx.fillText(firstLine, x, lineY, maxWidth);
          firstLine = word + ' ';
          firstLineCount += 1;
        } else {
          firstLine = testLine;
        }
      });

      firstLineCount += 1;
      const firstLineY = y + (firstLineCount - 1) * lineHeightBig;
      ctx.strokeText(firstLine, x, firstLineY, maxWidth);
      ctx.fillText(firstLine, x, firstLineY, maxWidth);

      y += firstLineCount * lineHeightBig + 20;

      if (finalShareText[1]) {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#B8860B';
        ctx.font = '35px Montserrat';

        const lineHeightSmall = 40;
        const secondLineWords = String(finalShareText[1] || '').split(' ');
        let secondLine = '';
        let secondLineCount = 0;

        secondLineWords.forEach((word, index) => {
          const testLine = secondLine + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && index > 0) {
            const lineY = y + secondLineCount * lineHeightSmall;
            ctx.fillText(secondLine, x, lineY, maxWidth);
            secondLine = word + ' ';
            secondLineCount += 1;
          } else {
            secondLine = testLine;
          }
        });

        const secondLineY = y + secondLineCount * lineHeightSmall;
        ctx.fillText(secondLine, x, secondLineY, maxWidth);
      }

      canvas.toBlob((blob) => {
        if (!blob) return;
        objectUrl = URL.createObjectURL(blob);
        setShareImageUrl((prevUrl) => {
          if (prevUrl) URL.revokeObjectURL(prevUrl);
          return objectUrl;
        });
      }, 'image/png');

      setShareImageBase64(canvas.toDataURL('image/png'));
    };

    if (finalShareText.length > 0) {
      createShareImage().catch((error) => {
        console.error('Failed to generate share image:', error);
      });
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [finalShareText]);

  useEffect(() => {
    if (isGuest || !userId) {
      return;
    }

    let cancelled = false;

    const loadReferralLink = async () => {
      try {
        const res = await fetchData('/api/referrals', 'POST', { userId });
        if (cancelled) return;
        setRefCode(res.refCode || '');
        setReferralLink(res.referralLink || '');
      } catch (_) {
        if (!cancelled) {
          setRefCode('');
          setReferralLink('');
        }
      }
    };

    loadReferralLink();

    return () => {
      cancelled = true;
    };
  }, [isGuest, userId]);

  const saveImageToCapacitor = async (base64Data) => {
    try {
      const base64Content = base64Data.replace(/^data:image\/png;base64,/, '');
      const fileName = `blablagame_${Date.now()}.png`;

      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64Content,
        directory: Directory.Cache,
      });

      return result.uri;
    } catch (error) {
      console.error('Failed to save share image file:', error);
      return null;
    }
  };

  const getNativeShareApi = async () => {
    try {
      const module = await import('@capacitor/share');
      if (module?.Share?.share) {
        return module.Share;
      }
    } catch (_) {
      // Fallback below.
    }

    const legacyShare = window?.Capacitor?.Plugins?.Share;
    if (legacyShare?.share) {
      return legacyShare;
    }

    return null;
  };

  const fallbackWebShare = async () => {
    if (navigator.share && shareImageUrl) {
      try {
        const response = await fetch(shareImageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'blablagame.png', { type: 'image/png' });

        await navigator.share({
          title: brandName,
          text: sharePayloadText,
          url: activeReferralLink || undefined,
          files: [file],
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }

    if (navigator.share) {
      await navigator.share({
        title: brandName,
        text: sharePayloadText,
        url: activeReferralLink || undefined,
      });
      return;
    }

    if (navigator.clipboard?.writeText && activeReferralLink) {
      await navigator.clipboard.writeText(sharePayloadText);
    }
  };

  const handleShare = async () => {
    if (isSharing) return;
    setIsSharing(true);

    try {
      if (Capacitor.isNativePlatform()) {
        const nativeShare = await getNativeShareApi();
        if (!nativeShare) {
          await fallbackWebShare();
          return;
        }

        const savedFileUri = shareImageBase64 ? await saveImageToCapacitor(shareImageBase64) : null;

        const options = {
          title: brandName,
          text: sharePayloadText,
          url: activeReferralLink || undefined,
        };

        try {
          if (savedFileUri) {
            await nativeShare.share({ ...options, files: [savedFileUri] });
          } else {
            await nativeShare.share(options);
          }
          return;
        } catch (error) {
          if (savedFileUri) {
            await nativeShare.share(options);
            return;
          }
          throw error;
        }
      }

      await fallbackWebShare();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const socialIcons = useMemo(
    () => [
      { src: instagramIcon, alt: 'Instagram' },
      { src: telegramIcon, alt: 'Telegram' },
      { src: whatsappIcon, alt: 'WhatsApp' },
      { src: facebookIcon, alt: 'Facebook' },
      { src: twitterIcon, alt: 'Twitter' },
    ],
    [],
  );

  return (
    <div className={styles.modalContent}>
      <img src={shareImageUrl || blablagame} alt={`${brandName} Game`} className={styles.gameImage} />
      <h2 className={styles.scoreText}>{shareTitle}</h2>

      <div className={styles.socialButtons}>
        {socialIcons.map((icon) => (
          <button
            key={icon.alt}
            onClick={handleShare}
            className={styles.iconButton}
            title={icon.alt}
            disabled={isSharing}
          >
            <img src={icon.src} alt={icon.alt} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default GameShareScreen;
